import { memo, useCallback, useState } from 'react'
import { useToast } from './Toast'
import LottieSvg from './LottieSvg'
import { formatBalance } from '../lib/balance'
import { safeShowAlert, safeSheetOpenHaptic } from '../lib/safeTelegram'
import { Button } from './figma'

const DEFAULT_SHARE_TEXT = 'Заходи в StarsShop — звёзды и подарки Telegram выгодно!'

function ReferralStat({ label, value }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 px-4 py-4">
      <p className="truncate text-[14px] font-[510] leading-4 tracking-[-0.01em] text-white/50">{label}</p>
      <p className="truncate text-[20px] font-[590] leading-6 tracking-[-0.01em] text-white">{value}</p>
    </div>
  )
}

function EarnPage({ user, profile, loading, error }) {
  const data = user || profile
  const [copied, setCopied] = useState(false)
  const { showToast } = useToast()

  const handleCopyLink = useCallback(async () => {
    const link = data?.referral_link
    if (!link) {
      safeShowAlert('Ссылка недоступна')
      return
    }

    safeSheetOpenHaptic()

    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      showToast('Ссылка скопирована')
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      safeShowAlert('Не удалось скопировать ссылку')
    }
  }, [data?.referral_link, showToast])

  const handleInvite = useCallback(async () => {
    const refLink = data?.referral_link
    if (!refLink) {
      safeShowAlert('Ссылка недоступна')
      return
    }

    safeSheetOpenHaptic()

    const shareText = data?.referral_share_text || DEFAULT_SHARE_TEXT
    const shareUrl =
      `https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent(shareText)}`
    const tg = window.Telegram?.WebApp

    try {
      if (typeof tg?.openTelegramLink === 'function') {
        tg.openTelegramLink(shareUrl)
        return
      }
    } catch (inviteError) {
      console.error('[EarnPage] openTelegramLink failed', inviteError)
    }

    try {
      if (typeof tg?.openLink === 'function') {
        tg.openLink(shareUrl)
        return
      }
    } catch (inviteError) {
      console.error('[EarnPage] openLink failed', inviteError)
    }

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          url: refLink,
          text: shareText,
        })
        return
      } catch (shareError) {
        if (shareError?.name === 'AbortError') {
          return
        }
        console.error('[EarnPage] navigator.share failed', shareError)
      }
    }

    try {
      await navigator.clipboard.writeText(refLink)
      safeShowAlert('Ссылка скопирована!')
    } catch {
      safeShowAlert('Не удалось поделиться ссылкой')
    }
  }, [data?.referral_link, data?.referral_share_text])

  if (loading) {
    return (
      <div className="flex w-full flex-col gap-6 pb-2">
        <div className="mx-auto h-32 w-32 animate-pulse rounded-2xl bg-white/5" />
        <div className="home-figma-card h-[152px] animate-pulse" />
        <div className="space-y-2">
          <div className="h-[54px] animate-pulse rounded-full bg-white/10" />
          <div className="h-[54px] animate-pulse rounded-full bg-white/5" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="home-figma-card rounded-2xl px-4 py-5 text-center text-sm text-[#FF8A99]">
        {error}
      </div>
    )
  }

  const referralsCount = data?.referrals_count ?? 0
  const referralPercent = data?.referral_percent ?? 30
  const referralEarnings = data?.referral_earnings ?? 0
  const referralLink = data?.referral_link ?? ''
  const referralBlocked = !!data?.referral_blocked

  if (referralBlocked) {
    return (
      <div className="flex w-full flex-col gap-6 pb-2">
        <section className="flex flex-col items-center pt-2 text-center">
          <div className="h-32 w-32 opacity-50">
            <LottieSvg lottieUrl="/images/lottie/json/referral.json" className="h-full w-full" lazy={false} />
          </div>
          <h1 className="mt-6 text-[24px] font-[590] leading-7 tracking-[-0.01em] text-white">
            Реферальная система заблокирована
          </h1>
          <p className="mt-3 max-w-[340px] text-[16px] font-normal leading-[22px] tracking-[-0.01em] text-white/50">
            Реферальная система была заблокирована для вас. Приглашение новых пользователей и начисление дохода недоступны.
          </p>
        </section>

        <section className="home-figma-card relative flex h-[152px] flex-col justify-center overflow-hidden" aria-label="Статистика реферальной программы">
          <div className="flex min-h-[76px] items-center">
            <ReferralStat label="Заработано" value={`${formatBalance(referralEarnings)} ₽`} />
          </div>
          <img loading="eager" src="/images/icons/figma/divider.svg" alt="" width="361" height="76" className="pointer-events-none absolute inset-x-0 top-[76px] z-[3] h-[76px] w-full" aria-hidden="true" />
          <div className="relative z-[4] flex min-h-[76px]">
            <ReferralStat label="Приглашено" value="0" />
            <ReferralStat label="Ваша комиссия" value={`${referralPercent}%`} />
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-6 pb-2">
      <section className="flex flex-col items-center pt-2 text-center">
        <div className="h-32 w-32">
          <LottieSvg lottieUrl="/images/lottie/json/referral.json" className="h-full w-full" lazy={false} />
        </div>
        <div className="mt-3 flex flex-col items-center gap-2">
          <h1 className="text-[24px] font-[590] leading-[28px] tracking-[-0.01em] text-white">
            Приводи друзей
          </h1>
          <p className="max-w-[361px] text-[16px] font-normal leading-[22px] tracking-[-0.01em] text-white/50">
            Зарабатывай {referralPercent}% с прибыли от их покупок. Отправьте приглашение напрямую или скопируйте и поделитесь ссылкой.
          </p>
        </div>
      </section>

      <section className="home-figma-card relative flex h-[152px] flex-col overflow-hidden" aria-label="Статистика реферальной программы">
        <div className="flex h-[76px] items-center">
          <ReferralStat label="Заработано" value={`${formatBalance(referralEarnings)} ₽`} />
        </div>
        <img loading="eager" src="/images/icons/figma/divider.svg" alt="" width="361" height="76" className="pointer-events-none absolute inset-x-0 top-[76px] z-[3] h-[76px] w-full" aria-hidden="true" />
        <div className="relative z-[4] flex h-[76px]">
          <ReferralStat label="Приглашено" value={String(referralsCount)} />
          <ReferralStat label="Ваша комиссия" value={`${referralPercent}%`} />
        </div>
      </section>

      <div className="flex w-full flex-col gap-2">
        <Button
          type="main"
          onClick={handleInvite}
          disabled={!referralLink}
        >
          Пригласить друга
        </Button>
        <Button
          type="neutral"
          kind="enable"
          onClick={handleCopyLink}
          disabled={!referralLink}
        >
          {copied ? 'Скопировано' : 'Копировать'}
        </Button>
      </div>
    </div>
  )
}

export default memo(EarnPage)
